<template>
  <!-- Intégré à la fenêtre « Modifier la boutique » (embedded) ou page seule -->
  <div :class="embedded ? '' : 'card'">
    <div v-if="!embedded" class="card-header">
      <div class="card-title">
        <h3 class="card-label">Coordonnées et identité</h3>
      </div>
      <div class="card-toolbar" v-if="$can('boutique_update')">
        <button @click="save" class="btn btn-sm btn-primary fw-bolder" :disabled="isLoading">
          <span v-if="!isLoading"><i class="bi bi-check2 me-1"></i>{{ $t('button.save') }}</span>
          <span v-else>{{ $t('loading') }} <span class="spinner-border spinner-border-sm align-middle ms-2"></span></span>
        </button>
      </div>
    </div>

    <div :class="embedded ? '' : 'card-body'">
      <!-- Propres à cette boutique : factures, e-mails et site e-commerce de la boutique -->
      <div class="notice d-flex bg-light-primary rounded border-primary border border-dashed p-4 mb-8">
        <i class="bi bi-info-circle fs-2 text-primary me-3"></i>
        <div class="fs-7 text-gray-700">
          Ces informations sont propres à <strong>{{ shop?.name }}</strong> : elles apparaissent sur ses factures,
          ses e-mails et son site. La raison sociale, l'IFU et le RCCM sont communs à la
          société (Paramètres → Société).
        </div>
      </div>

      <div class="row mb-8">
        <!-- Logo -->
        <div class="col-md-6 mb-4">
          <label class="form-label fw-semibold">Logo</label>
          <div class="d-flex align-items-center gap-4">
            <div class="symbol symbol-100px border rounded">
              <img v-if="previewLogo" :src="previewLogo" alt="logo" style="object-fit: contain" />
              <span v-else class="symbol-label"><i class="bi bi-image fs-1 text-gray-400"></i></span>
            </div>
            <input type="file" class="form-control form-control-solid" accept=".png, .jpg, .jpeg, .svg"
                   @change="onFile($event, 'logo')" :disabled="!$can('boutique_update')" />
          </div>
          <div class="text-muted fs-7 mt-2">PNG, JPG, SVG (max 2MB)</div>
        </div>

        <!-- Favicon -->
        <div class="col-md-6 mb-4">
          <label class="form-label fw-semibold">{{ $t('society.forms.favicon') }}</label>
          <div class="d-flex align-items-center gap-4">
            <div class="symbol symbol-60px border rounded">
              <img v-if="previewFavicon" :src="previewFavicon" alt="favicon" style="object-fit: contain" />
              <span v-else class="symbol-label"><i class="bi bi-image fs-3 text-gray-400"></i></span>
            </div>
            <input type="file" class="form-control form-control-solid" accept=".ico, .png, .jpg, .jpeg, .svg"
                   @change="onFile($event, 'favicon')" :disabled="!$can('boutique_update')" />
          </div>
          <div class="text-muted fs-7 mt-2">ICO, PNG, JPG, SVG (max 1MB)</div>
        </div>
      </div>

      <div class="separator separator-dashed mb-8"></div>

      <div class="row">
        <div class="col-md-6 mb-4">
          <label class="form-label">{{ $t('society.forms.addressComp') || 'Complément d\'adresse' }}</label>
          <input type="text" v-model="form.addressComp" class="form-control form-control-solid" :disabled="!$can('boutique_update')" />
        </div>
        <div class="col-md-6 mb-4">
          <label class="form-label">{{ $t('society.forms.phone2') || 'Téléphone secondaire' }}</label>
          <input type="text" v-model="form.society_phone2" class="form-control form-control-solid" :disabled="!$can('boutique_update')" />
        </div>
        <div class="col-md-4 mb-4">
          <label class="form-label">{{ $t('society.forms.city') }}</label>
          <input type="text" v-model="form.city" class="form-control form-control-solid" :disabled="!$can('boutique_update')" />
        </div>
        <div class="col-md-4 mb-4">
          <label class="form-label">{{ $t('society.forms.zipcode') }}</label>
          <input type="text" v-model="form.zipcode" class="form-control form-control-solid" :disabled="!$can('boutique_update')" />
        </div>
        <div class="col-md-4 mb-4">
          <label class="form-label">{{ $t('society.forms.country') }}</label>
          <select v-model="form.country_id" class="form-select form-select-solid" :disabled="!$can('boutique_update')">
            <option :value="null">—</option>
            <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from "@/plugins/i18n"

export default {
  name: 'ShopBranding',
  props: {
    shop: { type: Object, default: () => ({}) },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      isLoading: false,
      countries: [],
      form: { addressComp: '', city: '', zipcode: '', society_phone2: '', country_id: null },
      files: { logo: null, favicon: null },
      previewLogo: '',
      previewFavicon: '',
    }
  },
  async mounted() {
    this.$store.dispatch('countries/loadCountries')
      .then(r => { this.countries = r.data?.countries ?? [] })
      .catch(() => {})
    await this.load()
  },
  methods: {
    shopId() {
      return this.$route.params.id
    },
    async load() {
      try {
        const { data } = await window.axios.get(`shop/${this.shopId()}/branding`)
        this.fill(data.branding)
      } catch (e) {
        console.error(e)
      }
    },
    fill(branding) {
      if (!branding) return
      for (const key in this.form) {
        this.form[key] = branding[key] ?? (key === 'country_id' ? null : '')
      }
      this.previewLogo = branding.logo_url || ''
      this.previewFavicon = branding.favicon_url || ''
      this.files = { logo: null, favicon: null }
    },
    onFile(e, key) {
      const file = e.target.files[0]
      if (!file) return
      this.files[key] = file
      const url = URL.createObjectURL(file)
      if (key === 'logo') this.previewLogo = url
      else this.previewFavicon = url
    },
    // silent : appelé par la fenêtre « Modifier la boutique », qui affiche son propre message
    async save({ silent = false } = {}) {
      this.isLoading = true
      const formData = new FormData()
      for (const key in this.form) {
        if (this.form[key] !== null && this.form[key] !== undefined) formData.append(key, this.form[key])
      }
      if (this.files.logo) formData.append('logo', this.files.logo)
      if (this.files.favicon) formData.append('favicon', this.files.favicon)
      try {
        const { data } = await window.axios.post(`shop/${this.shopId()}/branding`, formData)
        this.fill(data.branding)
        if (!silent) window.toastr['success'](i18n.global.t('notification.society_updated'))
      } catch (e) {
        window.toastr['error'](e.response?.data?.message || i18n.global.t('notification.error'))
        if (silent) throw e
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
